<template>
  <div
    class="counseling-chat-container"
    :style="chatViewportHeight ? { '--chat-shell-height': `${chatViewportHeight}px` } : null"
  >
    <!-- Header Section -->
    <div class="chat-header">
      <div class="header-content">
        <h5 class="header-title">
          <i class="fas fa-headset text-success" />
          Counseling Support
        </h5>
        <p class="header-subtitle">
          Get help from our support team
        </p>
      </div>
      <div
        v-if="!openSession"
        class="header-action"
      >
        <BButton
          variant="success"
          :disabled="busy.requesting"
          class="request-btn"
          @click="requestSession"
        >
          <i
            :class="busy.requesting ? 'fas fa-hourglass-half' : 'fas fa-plus'"
            class="me-2"
          />
          {{ busy.requesting ? 'Starting...' : 'Start Session' }}
        </BButton>
      </div>
    </div>

    <!-- No Session State -->
    <div
      v-if="!openSession"
      class="empty-state"
    >
      <div class="empty-state-content">
        <i class="fas fa-comments empty-icon" />
        <h4>No Active Session</h4>
        <p>Start a counseling session to get support from our team</p>
      </div>
    </div>

    <!-- Session Active State -->
    <div
      v-else
      class="chat-wrapper"
    >
      <!-- Session Info Bar -->
      <div class="session-info-bar">
        <div class="session-details">
          <div class="session-status">
            <i
              :class="statusIcon"
              class="me-2"
            />
            <span :class="`status-${openSession.status}`">
              {{ openSession.status === 'pending' ? 'Waiting for Admin' : openSession.status }}
            </span>
          </div>
          <div
            v-if="openSession.status === 'pending'"
            class="queue-info"
          >
            <span class="queue-position">#{{ queuePosition || '...' }} in queue</span>
            <span class="available-admins">{{ availableAdmins }} available</span>
          </div>
          <div
            v-else-if="openSession.assigned_admin"
            class="admin-info"
          >
            Chatting with {{ openSession.assigned_admin.first_name }}
          </div>
        </div>
        <div class="session-actions">
          <BButton
            v-if="openSession.status !== 'closed'"
            size="sm"
            variant="outline-danger"
            :disabled="busy.closing"
            @click="closeSession"
          >
            <i class="fas fa-times me-1" />
            End
          </BButton>
        </div>
      </div>

      <!-- Messages Area -->
      <div
        ref="messagesContainer"
        class="messages-container"
      >
        <div
          v-if="!messages.length"
          class="messages-empty"
        >
          <template v-if="openSession.status === 'pending'">
            <div class="waiting-state">
              <i class="fas fa-hourglass-half waiting-icon" />
              <h5>Waiting for Admin Response</h5>
              <p>You are <strong>#{{ queuePosition || '?' }}</strong> in the queue</p>
              <p class="text-muted small">
                {{ availableAdmins }} counselor{{ availableAdmins !== 1 ? 's' : '' }} available
              </p>
              <div class="waiting-loader mt-3">
                <i class="fas fa-hourglass-half me-2" />
                <span>Please wait...</span>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="text-center text-muted">
              <i
                class="fas fa-comment-dots text-muted mb-2"
                style="font-size: 2rem;"
              />
              <p>No messages yet. Start the conversation!</p>
            </div>
          </template>
        </div>

        <div
          v-else
          class="messages-list"
        >
          <div
            v-for="msg in messages"
            :key="msg.id"
            :class="['message-group', msg.sender_type === 'student' ? 'sent' : 'received']"
          >
            <div class="message-bubble">
              <div class="message-header">
                <span class="sender-name">{{ msg.sender_name }}</span>
                <span class="message-time">{{ formatTime(msg.sent_at) }}</span>
              </div>
              <div
                v-if="msg.file_info"
                class="message-file"
              >
                <div
                  v-if="isImageAttachment(msg.file_info)"
                  class="image-preview-wrapper"
                >
                  <img
                    v-if="imagePreviewUrl(msg.file_info)"
                    :src="imagePreviewUrl(msg.file_info)"
                    :alt="displayFileName(msg.file_info)"
                    class="image-preview"
                    @click="openImagePreview(msg.file_info)"
                  >
                  <div
                    v-else
                    class="image-preview-loading"
                  >
                    <i class="fas fa-image" />
                    Loading preview...
                  </div>
                </div>

                <div class="file-row">
                  <i
                    :class="getFileIcon(msg.file_info.mime_type)"
                    class="file-type-icon"
                  />
                  <a
                    href="#"
                    class="file-link"
                    :title="extractFileName(msg.file_info)"
                    @click.prevent="downloadAttachment(msg.file_info)"
                  >
                    {{ displayFileName(msg.file_info) }}
                  </a>
                  <span
                    v-if="msg.file_info.size"
                    class="file-size"
                  >
                    {{ formatFileSize(msg.file_info.size) }}
                  </span>
                  <button
                    type="button"
                    class="download-btn"
                    :title="`Download ${extractFileName(msg.file_info)}`"
                    @click.prevent="downloadAttachment(msg.file_info)"
                  >
                    <i class="fas fa-download" />
                  </button>
                </div>
              </div>
              <div
                v-if="msg.message"
                class="message-text"
              >
                {{ msg.message }}
              </div>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div
            v-if="isOtherTyping"
            class="message-group received"
          >
            <div class="message-bubble">
              <div class="typing-indicator">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div
        v-if="openSession.status === 'active'"
        class="input-area"
      >
        <div class="input-wrapper">
          <div class="file-input-wrapper">
            <input
              ref="fileInput"
              type="file"
              class="file-input-hidden"
              @change="onFileSelected"
            >
            <BButton
              size="sm"
              variant="outline-secondary"
              :disabled="busy.uploading"
              class="attach-btn"
              title="Attach file or image"
              @click="$refs.fileInput.click()"
            >
              <i :class="busy.uploading ? 'fas fa-hourglass-half' : 'fas fa-paperclip'" />
            </BButton>
          </div>

          <BInputGroup class="message-input-group composer-group">
            <BFormInput
              v-model.trim="draftMessage"
              placeholder="Type your message..."
              maxlength="2000"
              :disabled="busy.sending || busy.uploading"
              class="message-input"
              @keyup.enter="sendMessage"
            />
            <BInputGroupAppend>
              <BButton
                variant="success"
                class="send-btn"
                :disabled="busy.sending || !draftMessage"
                @click="sendMessage"
              >
                <i class="fas fa-paper-plane me-1" />
                Send
              </BButton>
            </BInputGroupAppend>
          </BInputGroup>
        </div>
        <div
          v-if="uploadProgress > 0"
          class="upload-progress"
        >
          <div class="upload-progress-bar">
            <div
              class="upload-progress-fill"
              :style="{ width: uploadProgress + '%' }"
            />
          </div>
          <small class="upload-text">Uploading... {{ uploadProgress }}%</small>
        </div>
      </div>
    </div>

    <BModal
      id="counseling-feedback-modal"
      v-model="feedbackPrompt.show"
      title="Session Feedback"
      :no-close-on-backdrop="feedbackPrompt.submitting"
      :no-close-on-esc="feedbackPrompt.submitting"
      hide-footer
      body-class="feedback-modal-body"
    >
      <p class="feedback-intro">
        The session has ended. Please share your feedback to help us improve counseling support.
      </p>

      <BForm @submit.prevent="submitSessionFeedback">
        <BFormGroup
          label="Rating"
          label-class="fw-bold"
          class="mb-2"
        >
          <div
            class="rating-stars"
            role="radiogroup"
            aria-label="Session feedback rating"
          >
            <button
              v-for="star in 5"
              :key="`rating-${star}`"
              type="button"
              class="rating-star-btn"
              :class="{ active: star <= Number(feedbackPrompt.form.rating) }"
              :disabled="feedbackPrompt.submitting"
              :aria-label="`${star} star${star > 1 ? 's' : ''}`"
              @click="feedbackPrompt.form.rating = star"
            >
              <i :class="star <= Number(feedbackPrompt.form.rating) ? 'fas fa-star' : 'far fa-star'" />
            </button>
            <span class="rating-value">{{ feedbackPrompt.form.rating }}/5</span>
          </div>
        </BFormGroup>

        <BFormGroup
          label="Feedback Type"
          label-for="session-feedback-type"
          label-class="fw-bold"
          class="mb-2"
        >
          <BFormSelect
            id="session-feedback-type"
            v-model="feedbackPrompt.form.feedback_type"
            :disabled="feedbackPrompt.submitting"
            :options="feedbackTypeOptions"
          />
        </BFormGroup>

        <BFormGroup
          v-if="feedbackPrompt.form.feedback_type === 'other'"
          label="Specify Type"
          label-for="session-feedback-other"
          label-class="fw-bold"
          class="mb-2"
        >
          <BFormInput
            id="session-feedback-other"
            v-model.trim="feedbackPrompt.form.other_type"
            maxlength="255"
            :disabled="feedbackPrompt.submitting"
          />
        </BFormGroup>

        <BFormGroup
          label="Comments (Optional)"
          label-for="session-feedback-comments"
          label-class="fw-bold"
          class="mb-3"
        >
          <BFormTextarea
            id="session-feedback-comments"
            v-model.trim="feedbackPrompt.form.comments"
            :disabled="feedbackPrompt.submitting"
            rows="4"
            maxlength="1000"
            placeholder="Share your experience"
          />
        </BFormGroup>

        <div class="feedback-actions">
          <BButton
            variant="outline-secondary"
            :disabled="feedbackPrompt.submitting"
            @click="dismissSessionFeedback"
          >
            Later
          </BButton>
          <BButton
            type="submit"
            variant="success"
            :disabled="feedbackPrompt.submitting || !canSubmitSessionFeedback"
          >
            <i :class="feedbackPrompt.submitting ? 'fas fa-hourglass-half me-1' : 'fas fa-paper-plane me-1'" />
            {{ feedbackPrompt.submitting ? 'Submitting...' : 'Submit Feedback' }}
          </BButton>
        </div>
      </BForm>
    </BModal>
  </div>
</template>

<script>
import { SSharedCounselingChat } from '@/services'

const defaultSessionFeedbackForm = () => ({
  rating: 5,
  feedback_type: 'counseling',
  other_type: '',
  comments: ''
})

export default {
  name: 'StudentCounselingChat',

  middleware: ['auth', 'student'],

  metaInfo: () => ({ title: 'Counseling Chat' }),

  data () {
    return {
      chatViewportHeight: null,
      draftMessage: '',
      uploadingFile: null,
      uploadProgress: 0,
      openSession: null,
      queuePosition: null,
      availableAdmins: 0,
      messages: [],
      isOtherTyping: false,
      busy: {
        requesting: false,
        sending: false,
        closing: false,
        uploading: false
      },
      poller: null,
      queueStatusSubscription: null,
      sessionSubscription: null,
      studentSubscription: null,
      lastMessageId: null,
      imagePreviewUrls: {},
      loadingImagePreviewKeys: {},
      feedbackPrompt: {
        show: false,
        submitting: false,
        sessionId: null,
        form: defaultSessionFeedbackForm()
      },
      feedbackPromptHandledSessions: {},
      shouldPromptPendingFeedback: false
    }
  },

  computed: {
    statusIcon () {
      if (this.openSession?.status === 'pending') return 'fas fa-clock text-warning'
      if (this.openSession?.status === 'active') return 'fas fa-check-circle text-success'
      return 'fas fa-times-circle text-danger'
    },

    feedbackTypeOptions () {
      return [
        { value: 'counseling', text: 'Counseling' },
        { value: 'service', text: 'Service' },
        { value: 'other', text: 'Other' }
      ]
    },

    canSubmitSessionFeedback () {
      if (!this.feedbackPrompt.sessionId) return false
      if (!this.feedbackPrompt.form.rating || !this.feedbackPrompt.form.feedback_type) return false
      if (this.feedbackPrompt.form.feedback_type === 'other' && !this.feedbackPrompt.form.other_type) return false
      return true
    }
  },

  watch: {
    messages: {
      handler () {
        this.$nextTick(() => {
          this.scrollToBottom()
        })

        this.ensureImagePreviews(this.messages)
      },
      deep: true
    }
  },

  mounted () {
    this.lockChatToViewport()
    window.addEventListener('resize', this.lockChatToViewport, { passive: true })
    this.loadState()
    this.startPolling()
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.lockChatToViewport)
    this.stopPolling()
    this.unsubscribeAll()
    this.cleanupImagePreviews()
  },

  methods: {
    lockChatToViewport () {
      this.$nextTick(() => {
        const root = this.$el
        if (!root || typeof window === 'undefined') return

        const footer = document.querySelector('.iq-footer')
        const footerHeight = footer ? footer.offsetHeight : 0
        const topOffset = Math.max(root.getBoundingClientRect().top, 0)
        const reservedSpace = 28
        const availableHeight = window.innerHeight - topOffset - footerHeight - reservedSpace

        this.chatViewportHeight = availableHeight > 360 ? availableHeight : null
      })
    },

    async loadState () {
      try {
        const hadOpenSession = Boolean(this.openSession?.id)
        const { data: payload } = await SSharedCounselingChat.getState()
        const pendingFeedbackSession = payload?.data?.pending_feedback_session || null
        this.openSession = payload?.data?.open_session || null
        this.queuePosition = payload?.data?.queue_position || null
        this.availableAdmins = payload?.data?.available_admins || 0

        if (hadOpenSession && !this.openSession?.id) {
          this.shouldPromptPendingFeedback = true
        }

        if (this.openSession) {
          this.messages = this.openSession.messages || []
          this.lastMessageId = this.messages[this.messages.length - 1]?.id || null
        } else {
          this.messages = []
          this.lastMessageId = null
        }

        this.handlePendingFeedbackSession(pendingFeedbackSession)

        this.subscribeRealtime()
      } catch (error) {
        console.error('Failed to load state:', error)
      }
    },

    async requestSession () {
      this.busy.requesting = true
      try {
        const { data: payload } = await SSharedCounselingChat.requestSession()
        this.openSession = payload?.data || null
        if (this.openSession) {
          this.messages = this.openSession.messages || []
        }
        this.subscribeRealtime()
      } catch (error) {
        this.$bvToast?.toast('Failed to request session', { variant: 'danger' })
      } finally {
        this.busy.requesting = false
      }
    },

    async sendMessage (options = null) {
      if (!this.openSession?.id) return

      const isPayload = Boolean(
        options &&
        options.constructor === Object &&
        (Object.prototype.hasOwnProperty.call(options, 'message') || Object.prototype.hasOwnProperty.call(options, 'fileInfo'))
      )

      const message = isPayload
        ? (typeof options.message === 'string' ? options.message : '')
        : this.draftMessage

      const fileInfo = isPayload
        ? (options.fileInfo || null)
        : null

      if (!message && !fileInfo) return

      const optimisticMessage = {
        id: 'temp-' + Date.now(),
        sender_type: 'student',
        sender_id: this.$store.state.auth.user?.id || 0,
        sender_name: 'You',
        message: message,
        file_info: fileInfo,
        sent_at: new Date().toISOString(),
        optimistic: true
      }

      // Optimistic update
      this.messages.push(optimisticMessage)
      this.draftMessage = ''

      this.busy.sending = true
      try {
        const { data: payload } = await SSharedCounselingChat.sendMessage(this.openSession.id, message, fileInfo)
        const idx = this.messages.findIndex(m => m.id === optimisticMessage.id)
        if (idx !== -1) {
          this.messages[idx] = payload?.data?.message
          this.lastMessageId = payload?.data?.message?.id || this.lastMessageId
        }
      } catch (error) {
        // Remove optimistic message on error
        const idx = this.messages.indexOf(optimisticMessage)
        if (idx !== -1) {
          this.messages.splice(idx, 1)
        }
        this.$bvToast?.toast('Failed to send message', { variant: 'danger' })
      } finally {
        this.busy.sending = false
      }
    },

    async closeSession () {
      if (!this.openSession?.id) return

      const confirmed = await this.$bvModal?.msgBoxConfirm('End this counseling session?')
      if (!confirmed) return

      this.busy.closing = true
      try {
        await SSharedCounselingChat.closeSession(this.openSession.id, false)
        await this.loadState()
      } finally {
        this.busy.closing = false
      }
    },

    handlePendingFeedbackSession (pendingFeedbackSession) {
      const sessionId = pendingFeedbackSession?.session_id
      if (!sessionId) return
      if (this.openSession?.id) return
      if (this.feedbackPromptHandledSessions[sessionId]) return
      if (!this.shouldPromptPendingFeedback && this.feedbackPrompt.sessionId !== sessionId) return
      if (this.feedbackPrompt.show && this.feedbackPrompt.sessionId === sessionId) return

      this.feedbackPrompt.show = true
      this.feedbackPrompt.sessionId = sessionId
      if (!this.feedbackPrompt.form || Number(this.feedbackPrompt.form.rating) < 1 || Number(this.feedbackPrompt.form.rating) > 5) {
        this.feedbackPrompt.form = defaultSessionFeedbackForm()
      }
      this.shouldPromptPendingFeedback = false
    },

    dismissSessionFeedback () {
      if (this.feedbackPrompt.sessionId) {
        this.$set(this.feedbackPromptHandledSessions, this.feedbackPrompt.sessionId, true)
      }

      this.shouldPromptPendingFeedback = false
      this.feedbackPrompt.show = false
      this.feedbackPrompt.sessionId = null
      this.feedbackPrompt.form = defaultSessionFeedbackForm()
    },

    async submitSessionFeedback () {
      if (!this.canSubmitSessionFeedback || !this.feedbackPrompt.sessionId) {
        return
      }

      this.feedbackPrompt.submitting = true

      try {
        await SSharedCounselingChat.submitSessionFeedback(this.feedbackPrompt.sessionId, {
          rating: Number(this.feedbackPrompt.form.rating),
          feedback_type: this.feedbackPrompt.form.feedback_type,
          other_type: this.feedbackPrompt.form.feedback_type === 'other' ? this.feedbackPrompt.form.other_type : null,
          comments: this.feedbackPrompt.form.comments || null
        })

        this.$set(this.feedbackPromptHandledSessions, this.feedbackPrompt.sessionId, true)
        this.$bvToast?.toast('Thank you for sharing your feedback.', { variant: 'success' })

        this.shouldPromptPendingFeedback = false
        this.feedbackPrompt.show = false
        this.feedbackPrompt.sessionId = null
        this.feedbackPrompt.form = defaultSessionFeedbackForm()
        await this.loadState()
      } catch (error) {
        this.$bvToast?.toast(error?.response?.data?.message || 'Failed to submit feedback.', { variant: 'danger' })
      } finally {
        this.feedbackPrompt.submitting = false
      }
    },

    async onFileSelected (event) {
      const file = event.target.files?.[0]
      if (!file || !this.openSession?.id) return

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        this.$bvToast?.toast('File too large (max 10MB)', { variant: 'warning' })
        return
      }

      this.busy.uploading = true
      this.uploadProgress = 0

      try {
        const response = await SSharedCounselingChat.uploadFile(
          this.openSession.id,
          file,
          progressEvent => {
            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          }
        )

        if (response.data?.success) {
          const fileInfo = response.data.data
          await this.sendMessage({ message: '', fileInfo })
        }
      } catch (error) {
        this.$bvToast?.toast(error?.response?.data?.message || 'Failed to upload file', { variant: 'danger' })
      } finally {
        this.busy.uploading = false
        this.uploadProgress = 0
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
        }
      }
    },

    startPolling () {
      // Poll frequently so status/messages still update when Echo is unavailable.
      this.poller = setInterval(() => {
        if (this.busy.requesting || this.busy.sending || this.busy.uploading || this.busy.closing) {
          return
        }

        if (this.openSession?.id) {
          this.loadState()
        }
      }, 5000)
    },

    stopPolling () {
      if (this.poller) {
        clearInterval(this.poller)
        this.poller = null
      }
    },

    subscribeRealtime () {
      if (!window.Echo || !this.openSession?.id) {
        return
      }

      this.unsubscribeAll()

      const studentId = this.$store.state.auth.user?.id
      if (studentId) {
        this.studentSubscription = window.Echo.private(`counseling.student.${studentId}`)
          .listen('.counseling.session.requested', () => {
            this.loadState()
          })
          .listen('.counseling.session.accepted', () => {
            this.loadState()
          })
          .listen('.counseling.session.closed', () => {
            this.loadState()
          })
      }

      // Subscribe to session messages
      this.sessionSubscription = window.Echo.private(`counseling.session.${this.openSession.id}`)
        .listen('.counseling.message.sent', event => {
          if (event?.message && !this.hasMessage(event.message.id)) {
            this.messages.push(event.message)
            this.lastMessageId = event.message.id
          }
        })
        .listen('.counseling.session.accepted', () => {
          this.loadState()
        })
        .listen('.counseling.session.closed', () => {
          this.loadState()
        })

      // Subscribe to queue updates
      if (this.openSession.status === 'pending') {
        this.queueStatusSubscription = window.Echo.private('counseling.queue.status')
          .listen('.queue.status.updated', event => {
            this.availableAdmins = event?.available_admins || 0
            this.loadState()
          })
      }
    },

    unsubscribeAll () {
      if (this.sessionSubscription) {
        this.sessionSubscription.stopListening('.counseling.message.sent')
        this.sessionSubscription.stopListening('.counseling.session.accepted')
        this.sessionSubscription.stopListening('.counseling.session.closed')
        this.sessionSubscription = null
      }

      if (this.studentSubscription) {
        this.studentSubscription.stopListening('.counseling.session.requested')
        this.studentSubscription.stopListening('.counseling.session.accepted')
        this.studentSubscription.stopListening('.counseling.session.closed')
        this.studentSubscription = null
      }

      if (this.queueStatusSubscription) {
        this.queueStatusSubscription.stopListening('.queue.status.updated')
        this.queueStatusSubscription = null
      }
    },

    scrollToBottom () {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
      }
    },

    hasMessage (messageId) {
      if (!messageId) return false
      return this.messages.some(message => message.id === messageId)
    },

    extractFileName (fileInfo) {
      const fallbackName = 'attachment'
      const rawName = (fileInfo?.file_name || fileInfo?.stored_name || fallbackName).trim()

      // Remove generated UUID prefix when present in stored filenames.
      return rawName.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i, '')
    },

    truncateFileName (fileName, maxLength = 32) {
      if (!fileName) return 'attachment'
      if (fileName.length <= maxLength) return fileName

      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex <= 0 || dotIndex === fileName.length - 1) {
        return `${fileName.slice(0, maxLength - 3)}...`
      }

      const extension = fileName.slice(dotIndex)
      const baseName = fileName.slice(0, dotIndex)
      const maxBaseLength = Math.max(8, maxLength - extension.length - 3)

      return `${baseName.slice(0, maxBaseLength)}...${extension}`
    },

    displayFileName (fileInfo) {
      return this.truncateFileName(this.extractFileName(fileInfo))
    },

    isImageAttachment (fileInfo) {
      if (!fileInfo) return false

      const mimeType = (fileInfo.mime_type || '').toLowerCase()
      if (mimeType.startsWith('image/')) return true

      const fileName = this.extractFileName(fileInfo).toLowerCase()
      return /\.(png|jpe?g|gif|bmp|webp|svg)$/.test(fileName)
    },

    filePreviewKey (fileInfo) {
      return fileInfo?.stored_name || fileInfo?.file_url || fileInfo?.file_name || null
    },

    imagePreviewUrl (fileInfo) {
      const key = this.filePreviewKey(fileInfo)
      return key ? this.imagePreviewUrls[key] : null
    },

    openImagePreview (fileInfo) {
      const previewUrl = this.imagePreviewUrl(fileInfo)
      if (previewUrl) {
        window.open(previewUrl, '_blank', 'noopener,noreferrer')
        return
      }

      this.downloadAttachment(fileInfo)
    },

    ensureImagePreviews (messagesList = []) {
      if (!Array.isArray(messagesList)) return

      this.syncImagePreviewCache(messagesList)

      messagesList
        .filter(message => this.isImageAttachment(message.file_info))
        .forEach(message => {
          this.loadImagePreview(message.file_info)
        })
    },

    syncImagePreviewCache (messagesList = []) {
      const activeKeys = new Set(
        messagesList
          .filter(message => this.isImageAttachment(message.file_info))
          .map(message => this.filePreviewKey(message.file_info))
          .filter(Boolean)
      )

      Object.keys(this.imagePreviewUrls).forEach(key => {
        if (!activeKeys.has(key)) {
          window.URL.revokeObjectURL(this.imagePreviewUrls[key])
          this.$delete(this.imagePreviewUrls, key)
        }
      })

      Object.keys(this.loadingImagePreviewKeys).forEach(key => {
        if (!activeKeys.has(key)) {
          this.$delete(this.loadingImagePreviewKeys, key)
        }
      })
    },

    async loadImagePreview (fileInfo) {
      if (!this.openSession?.id || !fileInfo) return

      const key = this.filePreviewKey(fileInfo)
      if (!key || this.imagePreviewUrls[key] || this.loadingImagePreviewKeys[key]) {
        return
      }

      this.$set(this.loadingImagePreviewKeys, key, true)

      try {
        const storedName = this.resolveStoredName(fileInfo)
        const response = storedName
          ? await SSharedCounselingChat.downloadFile(this.openSession.id, storedName)
          : await this.$http.get(fileInfo.file_url, { responseType: 'blob' })

        const objectUrl = window.URL.createObjectURL(response.data)
        this.$set(this.imagePreviewUrls, key, objectUrl)
      } catch (error) {
        // Keep silent; attachment can still be downloaded manually.
      } finally {
        this.$delete(this.loadingImagePreviewKeys, key)
      }
    },

    cleanupImagePreviews () {
      Object.values(this.imagePreviewUrls).forEach(url => {
        window.URL.revokeObjectURL(url)
      })

      this.imagePreviewUrls = {}
      this.loadingImagePreviewKeys = {}
    },

    async downloadAttachment (fileInfo) {
      if (!this.openSession?.id) {
        this.$bvToast?.toast('No active session for this attachment.', { variant: 'warning' })
        return
      }

      const storedName = this.resolveStoredName(fileInfo)

      if (!storedName && !fileInfo?.file_url) {
        this.$bvToast?.toast('Attachment is unavailable.', { variant: 'warning' })
        return
      }

      try {
        const response = storedName
          ? await SSharedCounselingChat.downloadFile(this.openSession.id, storedName)
          : await this.$http.get(fileInfo.file_url, { responseType: 'blob' })

        const blobUrl = window.URL.createObjectURL(new Blob([response.data], {
          type: fileInfo.mime_type || 'application/octet-stream'
        }))

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = this.extractFileName(fileInfo)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        this.$bvToast?.toast('Failed to download attachment', { variant: 'danger' })
      }
    },

    resolveStoredName (fileInfo) {
      if (!fileInfo) return null

      if (fileInfo.stored_name) {
        return fileInfo.stored_name
      }

      if (!fileInfo.file_url) {
        return null
      }

      const marker = '/download-file/'
      const markerIndex = fileInfo.file_url.indexOf(marker)
      if (markerIndex === -1) {
        return null
      }

      const afterMarker = fileInfo.file_url.slice(markerIndex + marker.length)
      const rawStoredName = afterMarker.split('?')[0]

      try {
        return decodeURIComponent(rawStoredName)
      } catch (error) {
        return rawStoredName
      }
    },

    formatTime (value) {
      if (!value) return ''
      const date = new Date(value)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    formatFileSize (bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    getFileIcon (mimeType) {
      if (!mimeType) return 'fas fa-file'
      if (mimeType.startsWith('image/')) return 'fas fa-image'
      if (mimeType.startsWith('video/')) return 'fas fa-video'
      if (mimeType.startsWith('audio/')) return 'fas fa-file-audio'
      if (mimeType.includes('pdf')) return 'fas fa-file-pdf'
      if (mimeType.includes('word') || mimeType.includes('document')) return 'fas fa-file-word'
      if (mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv')) return 'fas fa-file-excel'
      if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'fas fa-file-archive'
      if (mimeType.includes('text') || mimeType.includes('json')) return 'fas fa-file-alt'
      return 'fas fa-file'
    }
  }
}
</script>

<style scoped lang="scss">
.counseling-chat-container {
  --chat-shell-height: calc(100dvh - 180px);
  display: flex;
  flex-direction: column;
  height: var(--chat-shell-height);
  max-height: var(--chat-shell-height);
  min-height: 0;
  background: linear-gradient(135deg, #1f4337 0%, #2d6a4f 60%, #95d5b2 100%);
  overflow: hidden;
}

.chat-header {
  padding: 24px;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 2px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  .header-content {
    .header-title {
      margin: 0 0 4px;
      color: #333;
      font-size: 1.25rem;
    }

    .header-subtitle {
      margin: 0;
      color: #999;
      font-size: 0.875rem;
    }
  }

  .header-action {
    .request-btn {
      min-height: 38px;
      padding: 8px 16px;
      font-weight: 600;
      border-radius: 8px;
    }
  }
}

.empty-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;

  .empty-state-content {
    text-align: center;
    color: white;

    .empty-icon {
      font-size: 4rem;
      margin-bottom: 20px;
      opacity: 0.8;
    }

    h4 {
      margin: 0 0 12px;
      font-size: 1.5rem;
    }

    p {
      margin: 0;
      opacity: 0.9;
    }
  }
}

.chat-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.session-info-bar {
  padding: 12px 20px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

  .session-details {
    display: flex;
    gap: 16px;
    align-items: center;

    .session-status {
      display: flex;
      align-items: center;
      font-weight: 600;

      &.status-pending { color: #ffc107; }
      &.status-active { color: #28a745; }
      &.status-closed { color: #dc3545; }
    }

    .queue-info {
      display: flex;
      gap: 12px;
      font-size: 0.875rem;

      .queue-position {
        font-weight: 600;
        color: #2d6a4f;
      }

      .available-admins {
        color: #999;
      }
    }

    .admin-info {
      color: #555;
      font-size: 0.9rem;
    }
  }

  .session-actions {
    display: flex;
    gap: 8px;
  }
}

.messages-container {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: white;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;

    &:hover {
      background: #555;
    }
  }
}

.messages-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #999;

  .waiting-state {
    text-align: center;

    .waiting-icon {
      font-size: 3rem;
      color: #ffc107;
      margin-bottom: 16px;
    }

    h5 {
      margin-bottom: 12px;
      color: #333;
    }

    p {
      margin-bottom: 8px;
    }

    .waiting-loader {
      display: inline-flex;
      align-items: center;
      color: #64748b;
      font-size: 0.86rem;
      font-weight: 600;
    }
  }
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-group {
  display: flex;
  margin-bottom: 8px;

  &.sent {
    justify-content: flex-end;

    .message-bubble {
      background: #2d6a4f;
      color: white;
    }
  }

  &.received {
    justify-content: flex-start;

    .message-bubble {
      background: #f0f0f0;
      color: #333;
    }
  }
}

.message-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 12px;
  word-wrap: break-word;
  animation: slideIn 0.3s ease-out;

  .message-header {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 0.8rem;
    opacity: 0.8;

    .sender-name {
      font-weight: 600;
    }

    .message-time {
      white-space: nowrap;
    }
  }

  .message-text {
    line-height: 1.4;
  }

  .message-file {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 6px;

    .image-preview-wrapper {
      .image-preview {
        display: block;
        max-width: 240px;
        max-height: 190px;
        border-radius: 8px;
        cursor: pointer;
        object-fit: cover;
        border: 1px solid rgba(255, 255, 255, 0.28);
      }

      .image-preview-loading {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        border-radius: 6px;
        font-size: 0.78rem;
        background: rgba(0, 0, 0, 0.12);
      }
    }

    .file-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      background: rgba(0, 0, 0, 0.12);
      border-radius: 8px;

      .file-type-icon {
        flex-shrink: 0;
      }

      .file-link {
        color: inherit;
        text-decoration: none;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
        flex: 1;

        &:hover {
          text-decoration: underline;
        }
      }

      .file-size {
        font-size: 0.72rem;
        opacity: 0.9;
        white-space: nowrap;
      }

      .download-btn {
        border: none;
        background: transparent;
        color: inherit;
        cursor: pointer;
        padding: 2px 4px;
        line-height: 1;
        opacity: 0.9;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  .typing-indicator {
    display: flex;
    gap: 4px;
    height: 12px;

    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: currentColor;
      animation: typing 1.4s infinite;

      &:nth-child(2) { animation-delay: 0.2s; }
      &:nth-child(3) { animation-delay: 0.4s; }
    }
  }
}

.input-area {
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);

  .input-wrapper {
    display: flex;
    gap: 8px;
    align-items: center;

    .file-input-wrapper {
      display: inline-flex;
      align-items: center;

      .file-input-hidden {
        display: none;
      }

      .attach-btn {
        width: 40px;
        height: 40px;
        min-width: 40px;
        min-height: 40px;
        padding: 0;
        border-radius: 6px;
        border-color: #f0b448;
        background: #fff9ee;
        color: #d29a2f;
      }
    }

    .message-input-group,
    .composer-group {
      flex: 1;
      margin-bottom: 0;

      ::v-deep .input-group-append {
        margin-left: 0;
      }

      .message-input {
        height: 40px;
        border-radius: 6px 0 0 6px;
        border: 1px solid #d9dee7;
        border-right: 0;
        background: #f4f6fa;
        padding: 0 12px;
        font-size: 0.92rem;

        &:focus {
          border-color: #2d6a4f;
          box-shadow: none;
          background: #ffffff;
        }
      }

      .send-btn {
        height: 40px;
        border-radius: 0 6px 6px 0;
        padding: 0 16px;
        font-weight: 600;
        border-color: #10b981;
      }
    }
  }

  .upload-progress {
    margin-top: 8px;

    .upload-progress-bar {
      height: 4px;
      background: #e0e0e0;
      border-radius: 2px;
      overflow: hidden;

      .upload-progress-fill {
        height: 100%;
        background: #2d6a4f;
        transition: width 0.3s ease;
      }
    }

    .upload-text {
      display: block;
      margin-top: 4px;
      color: #2d6a4f;
      text-align: center;
    }
  }
}

.feedback-modal-body {
  .feedback-intro {
    margin-bottom: 12px;
    color: #334155;
    font-size: 0.92rem;
  }

  .rating-stars {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .rating-star-btn {
    border: none;
    background: transparent;
    color: #cbd5e1;
    font-size: 1.35rem;
    line-height: 1;
    padding: 2px;
    cursor: pointer;

    &.active {
      color: #f59e0b;
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  .rating-value {
    margin-left: 6px;
    font-size: 0.84rem;
    color: #64748b;
    font-weight: 600;
  }

  .feedback-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes typing {
  0%, 60%, 100% {
    opacity: 0.3;
  }
  30% {
    opacity: 1;
  }
}

// Responsive
@media (max-width: 768px) {
  .counseling-chat-container {
    --chat-shell-height: calc(100dvh - 165px);
  }

  .chat-header {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  .message-bubble {
    max-width: 85%;
  }

  .session-info-bar {
    flex-direction: column;
    gap: 12px;
    text-align: center;

    .session-details {
      flex-direction: column;
      width: 100%;
    }
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
